import { ExternalLink, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import type { TodoTaskIntegration } from '@/types/todo';

interface LinkedClickUpPreviewProps {
  integration: TodoTaskIntegration;
  onOpenClickUp: () => void;
  onUnlink: () => void;
}

export function LinkedClickUpPreview({
  integration,
  onOpenClickUp,
  onUnlink,
}: LinkedClickUpPreviewProps) {
  const { metadata } = integration;
  const clickup = metadata.clickup;

  if (!clickup) {
    return null;
  }

  const avatarSrc = getBrandfetchLogoUrl('clickup.com');

  return (
    <div className="rounded-2xl border border-border/60 bg-muted/40 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="space-y-0.5 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0">
            {avatarSrc ? (
              <Avatar className="h-5 w-5 shrink-0">
                <AvatarImage src={avatarSrc} alt="ClickUp" />
                <AvatarFallback>CU</AvatarFallback>
              </Avatar>
            ) : null}
            <p className="text-sm font-semibold truncate">{clickup.subtaskName}</p>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {clickup.listName} → {clickup.taskName}
          </p>
          {clickup.status && (
            <p className="text-xs text-muted-foreground">
              Status: <span className="capitalize">{clickup.status}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={onOpenClickUp}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in ClickUp
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={onUnlink}
            title="Unlink ClickUp task"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
